// Production module helpers.
//
// DATA CONTRACT (reuses the existing shared backend — nothing parallel invented):
// - Product.productType  => "BAKERY" | "NON_BAKERY"  (the Bakery/Non-Bakery split)
// - Product.currentRecipeId / currentRecipeVersion / currentRecipeStatus  => approved recipe link
// - Recipe.ingredients: [{ ingredientId, name, unit, qty, rate, rateUnit }]  (qty is per recipe yield)
// - Recipe.yieldQty / yieldUnit / packaging[]                                (established structure)
// - ProductionPlan: { date, section, status, lines[], issued{}, stockReserved } (lines/issued are
//   free-form on the backend by design; the line shape below is documented, not a new backend model).
// - Reserve: POST /production-plans/{id}/reserve { items: [{ ingredientId, name, qty, unit }] }
//
// Production plan LINE shape used by this web UI (self-describing, read defensively):
//   { productId, sku, productName, productType, category,
//     recipeId, recipeVersion, plannedQty, unit, yieldQty, yieldUnit,
//     requirements: [{ ingredientId, name, unit, qtyPerYield, requiredQty }] }

import api from "@/lib/api";

// Products store `currentRecipeId` as the human recipe CODE (e.g. "REC-000020"),
// but the backend only resolves GET /recipes/{uuid}. The recipes list carries the
// full recipe (incl. ingredients) with both `id` (uuid) and `recipeNumber` (code),
// so resolve by uuid first and fall back to a code match against the list.
export async function fetchRecipe(codeOrId) {
  if (!codeOrId) return null;
  try {
    const { data } = await api.get(`/recipes/${codeOrId}`);
    if (data && data.id) return data;
  } catch (e) {
    if (e?.response?.status && e.response.status !== 404) throw e;
  }
  const { data: list } = await api.get(`/recipes`);
  const arr = Array.isArray(list) ? list : [];
  return arr.find((r) => r.recipeNumber === codeOrId || r.id === codeOrId) || null;
}

export const PRODUCT_TYPES = [
  { value: "BAKERY", label: "Bakery" },
  { value: "NON_BAKERY", label: "Non-Bakery" },
];

export function productTypeLabel(pt) {
  if (!pt) return "—";
  return sectionBucket(pt) === "bakery" ? "Bakery" : "Non-Bakery";
}

// Collapse any product type / plan section into the two business buckets.
// Bakery stays Bakery; everything else (Non-Bakery, Other, blank …) => non-bakery.
export function sectionBucket(s) {
  const key = String(s || "").toUpperCase().replace(/[\s-]/g, "_");
  return key === "BAKERY" ? "bakery" : "nonbakery";
}

// Human-friendly plan code (no UUIDs in normal flows).
export function planCode(plan) {
  if (!plan) return "";
  const d = plan.date ? new Date(plan.date) : null;
  const ymd = d && !isNaN(d.getTime())
    ? `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`
    : "";
  // Human-readable number is derived from the production DATE. Multiple plans can share a
  // date, so append a short stable suffix from the internal id to keep the displayed number
  // UNIQUE (the internal database id remains the separate source of truth).
  const suffix = plan.id ? String(plan.id).replace(/-/g, "").slice(-4).toUpperCase() : "";
  if (!ymd) return plan.id ? `PLN-${suffix}` : "Plan";
  return suffix ? `PLN-${ymd}-${suffix}` : `PLN-${ymd}`;
}

// A product can be produced only against an ACTIVE (approved) recipe.
export function hasApprovedRecipe(product) {
  if (!product) return false;
  const status = String(product.currentRecipeStatus || "").toUpperCase();
  return !!product.currentRecipeId && status === "ACTIVE";
}

// Scale recipe ingredient quantities to a planned output quantity.
export function explodeRecipe(recipe, plannedQty) {
  if (!recipe) return [];
  const y = Number(recipe.yieldQty) || 1;
  const planned = Number(plannedQty) || 0;
  const factor = y > 0 ? planned / y : 0;
  const lines = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  return lines.map((l) => {
    const qtyPerYield = Number(l.qty ?? l.quantity) || 0;
    return {
      ingredientId: l.ingredientId || l.id || "",
      name: l.name || "",
      unit: l.unit || "",
      qtyPerYield,
      requiredQty: +(qtyPerYield * factor).toFixed(4),
      rate: Number(l.rate) || 0,
    };
  });
}

// Sum requirements across many plan lines, grouped by ingredient.
export function aggregateRequirements(lines) {
  const map = new Map();
  (lines || []).forEach((ln) => {
    (ln.requirements || []).forEach((r) => {
      if (!r.ingredientId) return;
      const cur = map.get(r.ingredientId) || { ingredientId: r.ingredientId, name: r.name, unit: r.unit, qty: 0 };
      cur.qty = +(cur.qty + (Number(r.requiredQty) || 0)).toFixed(4);
      map.set(r.ingredientId, cur);
    });
  });
  return Array.from(map.values());
}

// Ledger-driven FG balance (never overwrite balances directly).
// Current FG = Opening + Production + Returns + Adjustments - Dispatch - Damage - Expiry
// Available  = Current - Reserved
export function fgBalance(m = {}) {
  const n = (x) => Number(x) || 0;
  const current =
    n(m.opening) + n(m.production) + n(m.returns) + n(m.adjustments) -
    n(m.dispatch) - n(m.damage) - n(m.expiry);
  const available = current - n(m.reserved);
  return { current: +current.toFixed(4), available: +available.toFixed(4) };
}

// FG quality/status model. Only SALEABLE is normally dispatchable.
export const FG_STATUSES = ["SALEABLE", "QA_REVIEW", "QUARANTINE", "DAMAGED", "EXPIRED"];
export function isDispatchable(status) {
  return String(status || "").toUpperCase().replace(/[\s-]/g, "_") === "SALEABLE";
}

// Compute a best-before/expiry date from a product's shelf life.
export function computeExpiry(mfgDateStr, shelfLife) {
  const days = parseInt(String(shelfLife || "").replace(/[^0-9]/g, ""), 10);
  if (!mfgDateStr || !days) return "";
  const d = new Date(mfgDateStr);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
